import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

export interface BreadcrumbProps {
  items: string[];
  /** Large display breadcrumb — the last segment carries the DS title accent. */
  titleGradient?: boolean;
  className?: string;
}

/**
 * Design system title accent (fix-list #1): the SECOND word of the page title
 * takes brand-indigo, whole — never a mid-word slice and never a gradient. A
 * one-word title ("Email", "Recall", "Forms") gets no accent. A leading
 * initialism ("AI Receptionist") accents the word after it, which is just the
 * second word, so the one rule covers it.
 */
function TitleAccent({ title }: { title: string }) {
  const words = title.split(' ');
  if (words.length < 2) {
    return <span className="text-[var(--text-primary)]">{title}</span>;
  }
  return (
    <>
      <span className="text-[var(--text-primary)]">{words[0]} </span>
      <span className="text-[var(--brand-indigo)]">{words[1]}</span>
      {words.length > 2 ? (
        <span className="text-[var(--text-primary)]"> {words.slice(2).join(' ')}</span>
      ) : null}
    </>
  );
}

export function Breadcrumb({ items, titleGradient = false, className }: BreadcrumbProps) {
  if (titleGradient) {
    return (
      <nav
        className={cn('flex min-w-0 items-center gap-2.5', className)}
        aria-label="Breadcrumb"
      >
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <React.Fragment key={`${item}-${index}`}>
              <span className="whitespace-nowrap font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.025em] text-[var(--text-primary)]">
                {last ? <TitleAccent title={item} /> : item}
              </span>
              {!last ? <Icon kind="right" size={16} className="shrink-0 text-[var(--ink-4)]" /> : null}
            </React.Fragment>
          );
        })}
      </nav>
    );
  }

  return (
    <nav className={cn('flex items-center gap-2 text-[13px]', className)} aria-label="Breadcrumb">
      {items.map((item, index) => (
        <React.Fragment key={`${item}-${index}`}>
          <span
            className={cn(
              index === items.length - 1
                ? 'font-semibold text-[var(--ink-0)]'
                : 'font-medium text-[var(--ink-3)]',
            )}
          >
            {item}
          </span>
          {index < items.length - 1 ? (
            <Icon kind="right" size={12} className="text-[var(--ink-4)]" />
          ) : null}
        </React.Fragment>
      ))}
    </nav>
  );
}
